/**
 * Places — the household's named geofences (Home, School, Work, Grandma's).
 *
 * Nothing about a place lives in code: its name, where it is, how big it is, its
 * color. The Where screen labels a location fix with the place it falls inside,
 * so adding "School" here means the map and the log start saying "School" instead
 * of coordinates — no deploy. The Worker is the gate (adults only); this is the UI.
 *
 * No geocoder by design (same call as the weather panel): a plain lat/lng form.
 * On a failed save the fields keep what was typed, so nothing has to be re-entered.
 */
import { useCallback, useEffect, useState } from 'react';
import type { Place, PlaceInput } from '../../shared/types';
import { ApiRequestError, api } from '../../lib/api';
import { ConfigTable } from './ConfigTable';
import { ColorField, isHex } from './ColorField';
import { AlertNote, Button, Dialog, Field, Pill, TextInput } from './ui';

const DEFAULT_COLOR = '#2f6f4e';
const DEFAULT_RADIUS_M = 150;
const MIN_RADIUS_M = 10;
const MAX_RADIUS_M = 100_000; // matches the server's cap, so an oversized radius fails inline

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError) return err.message || fallback;
  return fallback;
}

export function PlacesPanel({ onStatus }: { onStatus: (message: string) => void }) {
  const [items, setItems] = useState<Place[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Place | 'new' | null>(null);
  const [confirming, setConfirming] = useState<Place | null>(null);
  const [busy, setBusy] = useState(false);

  function openEditor(place: Place | 'new') {
    setFormError(null);
    setEditing(place);
  }
  function closeEditor() {
    setFormError(null);
    setEditing(null);
  }
  function openConfirm(place: Place) {
    setConfirmError(null);
    setConfirming(place);
  }
  function closeConfirm() {
    setConfirmError(null);
    setConfirming(null);
  }

  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await api.get<Place[]>(`/places${showInactive ? '?include_inactive=1' : ''}`);
      setItems(rows);
    } catch (err) {
      setItems(null);
      setError(errorText(err, "Couldn't load your places."));
    }
  }, [showInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Optimistic: the row moves now, D1 catches up. A failure snaps it back. */
  async function reorder(ids: number[]) {
    if (!items) return;
    const byId = new Map(items.map((p) => [p.id, p]));
    const moved = ids.map((id) => byId.get(id)).filter((p): p is Place => Boolean(p));
    const untouched = items.filter((p) => !ids.includes(p.id));
    setItems([...moved, ...untouched]);
    setActionError(null);
    try {
      await api.put<Place[]>('/places/reorder', { ids });
      onStatus('Order saved.');
      await load();
    } catch (err) {
      setActionError(errorText(err, "Couldn't save the new order."));
      await load();
    }
  }

  async function save(input: PlaceInput) {
    setBusy(true);
    setFormError(null);
    try {
      if (editing && editing !== 'new') {
        await api.put<Place>(`/places/${editing.id}`, input);
        onStatus(`Saved ${input.name}.`);
      } else {
        await api.post<Place>('/places', input);
        onStatus(`Added ${input.name}.`);
      }
      closeEditor();
      await load();
    } catch (err) {
      setFormError(errorText(err, "Couldn't save that place."));
    } finally {
      setBusy(false);
    }
  }

  async function setActive(place: Place, active: boolean) {
    setBusy(true);
    if (active) setActionError(null);
    else setConfirmError(null);
    try {
      if (active) {
        await api.put<Place>(`/places/${place.id}`, { active: true });
        onStatus(`${place.name} is back.`);
      } else {
        await api.del<Place>(`/places/${place.id}`);
        onStatus(`${place.name} turned off. Past fixes keep the label.`);
        closeConfirm();
      }
      await load();
    } catch (err) {
      const message = errorText(err, "Couldn't update that place.");
      if (active) setActionError(message);
      else setConfirmError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="places-heading" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 id="places-heading" className="font-display text-xl font-semibold text-ink">
            Places
          </h2>
          <p className="mt-1 max-w-prose text-sm text-ink-soft">
            Name the spots that matter — Home, School, Work. When someone's phone is inside one,
            the Where screen says the place name instead of a set of coordinates.
          </p>
        </div>
        <Button variant="primary" onClick={() => openEditor('new')}>
          <span aria-hidden="true">＋</span> Add place
        </Button>
      </div>

      {actionError && <AlertNote message={actionError} />}

      <ConfigTable<Place>
        items={items}
        error={error}
        onRetry={() => void load()}
        keyOf={(p) => p.id}
        labelOf={(p) => p.name}
        isInactive={(p) => !p.active}
        onReorder={(ids) => void reorder(ids)}
        empty={{
          title: 'No places yet',
          body: 'A place is a spot with a name — Home, School, Grandma’s. Add your first one and fixes inside it read as that place instead of coordinates.',
          actionLabel: 'Add your first place',
          onAction: () => openEditor('new'),
        }}
        renderRow={(place) => (
          <div className="flex min-w-0 items-center gap-3">
            <span
              aria-hidden="true"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-full border border-border text-sm"
              style={{ backgroundColor: place.color }}
            >
              {place.icon ?? ''}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate font-medium text-ink">{place.name}</span>
                {!place.active && <Pill>Turned off</Pill>}
              </div>
              <p className="text-sm text-ink-soft tabular-nums">
                {place.radius_m} m radius
                <span className="text-ink-faint">
                  {' · '}
                  {place.lat.toFixed(4)}, {place.lng.toFixed(4)}
                </span>
              </p>
            </div>
          </div>
        )}
        renderActions={(place) => (
          <>
            <Button variant="ghost" onClick={() => openEditor(place)}>
              Edit
              <span className="sr-only"> {place.name}</span>
            </Button>
            {place.active ? (
              <Button variant="ghost" onClick={() => openConfirm(place)}>
                Turn off
                <span className="sr-only"> {place.name}</span>
              </Button>
            ) : (
              <Button variant="secondary" disabled={busy} onClick={() => void setActive(place, true)}>
                Restore
                <span className="sr-only"> {place.name}</span>
              </Button>
            )}
          </>
        )}
      />

      <label className="flex min-h-11 w-fit items-center gap-2 text-sm text-ink-soft">
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
          className="h-5 w-5 rounded border-border accent-sage focus-visible:ring-2 focus-visible:ring-sage focus-visible:outline-none"
        />
        Show turned-off places
      </label>

      {editing && (
        <PlaceDialog
          place={editing === 'new' ? null : editing}
          busy={busy}
          error={formError}
          onCancel={closeEditor}
          onSave={(input) => void save(input)}
        />
      )}

      {confirming && (
        <Dialog
          title={`Turn off ${confirming.name}?`}
          description="It stops labelling new location fixes. Nothing in your history changes — old fixes keep this place, and you can restore it any time."
          onClose={closeConfirm}
        >
          <div className="flex flex-col gap-4">
            {confirmError && <AlertNote message={confirmError} focusOnShow />}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="secondary" onClick={closeConfirm}>
                Keep it
              </Button>
              <Button variant="danger" disabled={busy} onClick={() => void setActive(confirming, false)}>
                {busy ? 'Turning off…' : 'Turn it off'}
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </section>
  );
}

function PlaceDialog({
  place,
  busy,
  error,
  onCancel,
  onSave,
}: {
  place: Place | null;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (input: PlaceInput) => void;
}) {
  const [name, setName] = useState(place?.name ?? '');
  const [lat, setLat] = useState(place ? String(place.lat) : '');
  const [lng, setLng] = useState(place ? String(place.lng) : '');
  const [radius, setRadius] = useState(place ? String(place.radius_m) : String(DEFAULT_RADIUS_M));
  const [icon, setIcon] = useState(place?.icon ?? '');
  const [color, setColor] = useState(place?.color ?? DEFAULT_COLOR);
  const [errors, setErrors] = useState<{
    name?: string;
    lat?: string;
    lng?: string;
    radius?: string;
    color?: string;
  }>({});

  function submit() {
    const latN = Number(lat.trim());
    const lngN = Number(lng.trim());
    const radiusN = Number(radius.trim());
    const next: typeof errors = {};

    if (!name.trim()) next.name = 'Give the place a name.';
    if (!Number.isFinite(latN) || latN < -90 || latN > 90)
      next.lat = 'Latitude must be between -90 and 90.';
    if (!Number.isFinite(lngN) || lngN < -180 || lngN > 180)
      next.lng = 'Longitude must be between -180 and 180.';
    if (!Number.isInteger(radiusN) || radiusN < MIN_RADIUS_M || radiusN > MAX_RADIUS_M)
      next.radius = `Radius must be a whole number of metres, ${MIN_RADIUS_M}–${MAX_RADIUS_M}.`;
    if (!isHex(color)) next.color = 'Pick a swatch or enter a hex like #2f6f4e.';

    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onSave({
      name: name.trim(),
      lat: latN,
      lng: lngN,
      radius_m: radiusN,
      icon: icon.trim() || null,
      color: color.trim().toLowerCase(),
    });
  }

  return (
    <Dialog
      title={place ? `Edit ${place.name}` : 'Add a place'}
      description="A fix inside the radius reads as this place instead of coordinates."
      onClose={onCancel}
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {error && <AlertNote message={error} focusOnShow />}

        <Field label="Name" error={errors.name}>
          {(props) => (
            <TextInput
              {...props}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Home"
              maxLength={40}
              required
            />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Latitude" error={errors.lat}>
            {(props) => (
              <TextInput
                {...props}
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                // Not inputMode="decimal": a phone's decimal keypad has no minus
                // key, so a southern latitude / western longitude can't be typed.
                inputMode="text"
                autoCapitalize="off"
                autoCorrect="off"
                placeholder="35.2271"
                autoComplete="off"
                required
              />
            )}
          </Field>
          <Field label="Longitude" error={errors.lng}>
            {(props) => (
              <TextInput
                {...props}
                value={lng}
                onChange={(e) => setLng(e.target.value)}
                inputMode="text"
                autoCapitalize="off"
                autoCorrect="off"
                placeholder="-80.8431"
                autoComplete="off"
                required
              />
            )}
          </Field>
        </div>

        <p className="text-xs leading-relaxed text-ink-faint">
          In a maps app, press and hold the spot — the coordinates come up as two decimal numbers,
          latitude then longitude. In the US the longitude is negative, so keep the minus sign
          (e.g. −80.84).
        </p>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Radius (metres)" hint="How close counts as here. 150 is a house." error={errors.radius}>
            {(props) => (
              <TextInput
                {...props}
                value={radius}
                onChange={(e) => setRadius(e.target.value)}
                inputMode="numeric"
                placeholder="150"
                required
              />
            )}
          </Field>
          <Field label="Icon" hint="Optional emoji, e.g. 🏡">
            {(props) => (
              <TextInput
                {...props}
                value={icon}
                onChange={(e) => setIcon(e.target.value)}
                placeholder="🏡"
                maxLength={8}
                autoComplete="off"
              />
            )}
          </Field>
        </div>

        <ColorField
          label="Color"
          hint="Used for this place on the map and here."
          value={color}
          onChange={setColor}
          error={errors.color}
        />

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : place ? 'Save changes' : 'Add place'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
